// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { getMainNavigationWidget } from "@pace/App/Store/Selectors/WidgetSelector";
import { checkoutPages } from "@pace/XDL/constants";
import IconSearch from "@pace/XDL/Icons/IconSearch";
import IconX from "@pace/XDL/Icons/IconX";
import { parseBoolean } from "@pace/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billTo = getBillToState(state, cartState.value ? cartState.value.billToId : undefined).value;

    return {
        isMobileScreen: (state as CustomAppState).xCommon.isSmallMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        mainNavigation: getMainNavigationWidget(state),
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        customerLabel: billTo?.label,
        customerPhone: billTo?.phone,
        accountNeedsReview: cartState.value?.properties.accountNeedsReview,
    };
};

// XNG-Changes
const mapDispatchToProps = {
    setNavDrawerIsOpen,
};

// XNG-Changes
type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    // XNG-Changes
    actionsWrapper?: InjectableCss;
    mobileSearch?: ClickableProps;
    accountContainer?: InjectableCss;
    customerPhone?: LinkPresentationProps;
    titleTypography?: TypographyProps;
    titleLabelTypography?: TypographyProps;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<Props> = ({
    id,
    // XNG-Changes
    isMobileScreen,
    isTabletScreen,
    mainNavigation,
    isCheckoutPage,
    customerLabel,
    customerPhone,
    accountNeedsReview,
    setNavDrawerIsOpen,
}) => {
    // XNG-Changes
    const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);

    React.useEffect(() => {
        setMobileSearchOpen(false);
    }, [isMobileScreen, isTabletScreen]);

    // XNG-Changes: Checkout Header
    if (isCheckoutPage) {
        return (
            <StyledWrapper {...logoLinksSearchStyles.container} {...{ isCheckout: true }}>
                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                    <Zone contentId={id} zoneName="Logo" />
                </StyledWrapper>
                {customerLabel && (
                    <StyledWrapper {...logoLinksSearchStyles.accountContainer}>
                        <Typography {...logoLinksSearchStyles.titleTypography} data-test-selector="header_shipToLabel">
                            <Typography {...logoLinksSearchStyles.titleLabelTypography}>
                                {translate("Account")}:
                            </Typography>
                            {customerLabel}
                        </Typography>
                    </StyledWrapper>
                )}

                {parseBoolean(accountNeedsReview) && (
                    <Typography
                        {...logoLinksSearchStyles.titleTypography}
                        data-test-selector="header_customerPurchaseWarning"
                    >
                        {siteMessage("Custom_CheckoutCustomerPurchaseWarning")}
                    </Typography>
                )}

                {createWidgetElement("Common/HeaderWarehouseInformation", { fields: {} })}
            </StyledWrapper>
        );
    }

    // XNG-Changes
    const handleMobileSearchClick = () => {
        setMobileSearchOpen(!mobileSearchOpen);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };

    return (
        // XNG-Changes: structure
        <StyledWrapper {...logoLinksSearchStyles.container}>
            <StyledWrapper {...logoLinksSearchStyles.logoWrapper} {...{ mobileSearchOpen }}>
                {isTabletScreen &&
                    createWidgetElement("Header/MainNavigation", {
                        fields: { ...mainNavigation },
                    })}
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            {(mobileSearchOpen || !isMobileScreen) && (
                <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                    <Zone contentId={id} zoneName="Search" />
                </StyledWrapper>
            )}
            <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                {isMobileScreen && (
                    <Clickable
                        onClick={handleMobileSearchClick}
                        {...logoLinksSearchStyles.mobileSearch}
                        {...{ mobileSearchOpen }}
                        aria-label="Header Search"
                    >
                        <Icon
                            color="secondary"
                            src={mobileSearchOpen ? IconX : IconSearch}
                            size={mobileSearchOpen ? 14 : 20}
                            className="menu-icon"
                        />
                    </Clickable>
                )}
                {!mobileSearchOpen && <Zone contentId={id} zoneName="actions" />}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    // XNG-Changes
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
